// owner: W2 — zod schemas + inferred types shared by api and web
// Unified search (people, jobs, orgs, posts) — GET /api/v1/search.
import { z } from 'zod';
import { UlidSchema } from './common';
import { PersonRefSchema } from './network';
import { VerifiedBadgeSchema } from './verification';

export const SEARCH_TYPES = ['people', 'jobs', 'orgs', 'posts'] as const;
export const SearchTypeSchema = z.enum(SEARCH_TYPES);
export type SearchType = z.infer<typeof SearchTypeSchema>;

export const SearchQuerySchema = z.object({
  q: z.string().trim().min(1, 'q must not be empty').max(200),
  /** Comma-separated subset of people|jobs|orgs|posts; default all four. */
  types: z
    .string()
    .trim()
    .min(1)
    .max(64)
    .transform((s) => [...new Set(s.split(',').map((t) => t.trim()))])
    .pipe(z.array(SearchTypeSchema).min(1))
    .optional()
    .transform((v) => v ?? [...SEARCH_TYPES]),
  /** Opaque offset cursor returned as `nextCursor`. */
  cursor: z.string().min(1).max(512).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type SearchQuery = z.output<typeof SearchQuerySchema>;

export const SearchPersonItemSchema = z.object({
  kind: z.literal('person'),
  user: PersonRefSchema,
  headline: z.string(),
  /** PRIVACY: redacted (domain only, never the full verified email). */
  verifiedBadges: z.array(VerifiedBadgeSchema),
  /** Mutual accepted connections with the viewer (0 for anonymous callers). */
  mutualCount: z.number().int().nonnegative(),
  /** 1st / 2nd / 3rd+ degree from the viewer (3 for anonymous callers). */
  degree: z.union([z.literal(1), z.literal(2), z.literal(3)]),
});
export const SearchJobItemSchema = z.object({
  kind: z.literal('job'),
  id: UlidSchema,
  slug: z.string(),
  title: z.string(),
  orgName: z.string(),
  location: z.string(),
});
export const SearchOrgItemSchema = z.object({
  kind: z.literal('org'),
  id: UlidSchema,
  slug: z.string(),
  name: z.string(),
  logoUrl: z.string().nullable(),
});
export const SearchPostItemSchema = z.object({
  kind: z.literal('post'),
  id: UlidSchema,
  author: PersonRefSchema,
  bodyExcerpt: z.string(),
});

export const SearchItemSchema = z.discriminatedUnion('kind', [
  SearchPersonItemSchema,
  SearchJobItemSchema,
  SearchOrgItemSchema,
  SearchPostItemSchema,
]);
export type SearchItem = z.infer<typeof SearchItemSchema>;

export const SearchResultSchema = z.object({
  items: z.array(SearchItemSchema),
  nextCursor: z.string().nullable(),
});
export type SearchResult = z.infer<typeof SearchResultSchema>;
